import type { BrokerClientPolicy } from './client-policy';

/**
 * Summary projection of a broker client — what crosses the API boundary
 * to the frontend. clientSecret is absent: a TypeScript-level guard
 * against accidentally leaking secrets in list/detail responses.
 */
export interface BrokerClientPolicySummary {
  clientId: string;
  redirectUris: string[];
  allowedGroups: number[];
  /** 'allow' (default when absent) or 'deny' — see BrokerClientPolicy.groupMode. */
  groupMode?: 'allow' | 'deny';
  /** Editable cosmetic label; clientId is immutable. */
  displayName?: string;
  aliasClaims?: Record<string, true>;
  hasSecret: true;
  lastUpdatedAt?: string;
  /**
   * Registry-only metadata fields (label, downstreamMode, infisicalFolder)
   * are NOT stored in KV — they only live in scripts/clients.config.ts and
   * its generated snapshot. The admin UI joins KV records against the
   * snapshot client-side to display them in the list/detail views. These
   * fields are populated on the server only when a snapshot lookup is
   * available (currently always undefined; Plan 2 may add server-side join).
   */
  label?: string;
  downstreamMode?: 'cloudflare-access-shared' | 'direct-oidc';
}

export function toSummary(policy: BrokerClientPolicy): BrokerClientPolicySummary {
  return {
    clientId: policy.clientId,
    redirectUris: policy.redirectUris,
    allowedGroups: policy.allowedGroups,
    ...(policy.groupMode !== undefined ? { groupMode: policy.groupMode } : {}),
    ...(policy.displayName !== undefined ? { displayName: policy.displayName } : {}),
    aliasClaims: policy.aliasClaims,
    hasSecret: true,
  };
}

export interface TombstoneSummary {
  clientId: string;
  deletedAt: string;
  deletedBy: number;
  hardDeleteAt: string;
  /**
   * Policy is the SUMMARY shape (no clientSecret) — list responses must
   * never return tombstoned clients' secrets. The restore endpoint
   * (Plan 2) reads the full record from KV separately.
   */
  policy: BrokerClientPolicySummary;
}

export interface ListClientsResponse {
  clients: BrokerClientPolicySummary[];
  tombstones: TombstoneSummary[];
  totalCount: number;
}

export interface CreateClientRequest {
  clientId: string;
  redirectUris: string[];
  allowedGroups: number[];
  groupMode?: 'allow' | 'deny';
  displayName?: string;
  aliasClaims?: Record<string, true>;
  /**
   * Registry-only metadata. These two fields are NOT persisted to KV —
   * the bridge only reads clientId / clientSecret / redirectUris /
   * allowedGroups / aliasClaims from BROKER_CLIENTS. `label` and
   * `downstreamMode` live in `scripts/clients.config.ts` and its
   * generated snapshot for the drift dashboard's client-side join.
   * Accepting them on the create request keeps the API surface forward-
   * compatible with a future server-side registry write, and lets the
   * audit log capture operator intent even though it doesn't survive
   * the round-trip. Until that future write exists, operators must
   * still hand-edit clients.config.ts to make the metadata visible to
   * the drift report.
   */
  label?: string;
  downstreamMode?: 'cloudflare-access-shared' | 'direct-oidc';
}

export interface CreateClientResponse {
  client: BrokerClientPolicySummary;
  secret: string;
}

export interface DriftReport {
  asOf: string;
  configSource: string;
  rows: DriftRow[];
}

export interface DriftRow {
  clientId: string;
  status: 'match' | 'kv-only' | 'config-only' | 'field-mismatch' | 'corrupt';
  fieldDiffs?: Array<{ field: string; kvValue: unknown; configValue: unknown }>;
  /** Populated when status === 'corrupt' — surfaces records that failed shape validation. */
  corruptionReason?: string;
}

/**
 * Response shape of GET /api/me (admin/functions/api/me.ts). No expiresAt:
 * the session JWT's exp claim is consumed by the middleware's verifySession()
 * and not exposed. `email` is null when the forum member has no email on
 * file — the bridge only emits the claim when Invision provides one.
 */
export interface SessionInfo {
  sub: string;
  forumMemberId: number;
  email: string | null;
  name: string;
  primaryGroup: number;
  /**
   * Effective resource→actions map for the session's primary group, resolved
   * by the middleware (super-admin env groups get full CRUD on everything).
   * Drives UI gating only — every endpoint re-checks server-side.
   */
  permissions: RbacPermissionSet;
  /** True when primaryGroup is in BRIDGE_SUPERADMIN_GROUPS (env bootstrap). */
  isSuperAdmin: boolean;
}

// ---------------------------------------------------------------------------
// RBAC — resource × CRUD permission matrix (v1.1)
// ---------------------------------------------------------------------------

export const RBAC_RESOURCES = [
  'clients',
  'tombstones',
  'secrets',
  'permissions',
  'changelog',
] as const;
export type RbacResource = (typeof RBAC_RESOURCES)[number];

/** Subset of the letters C, R, U, D (canonical order, no duplicates). */
export type RbacActions = string;

/** Effective permissions for one group: resource → "CRUD"-subset string. */
export type RbacPermissionSet = Partial<Record<RbacResource, RbacActions>>;

/**
 * The editable permission matrix stored in KV under `rbac:matrix`.
 * Keys of `groups` are forum group IDs as strings. Super-admin env groups
 * are NOT stored here — they are env-derived and always full-access.
 */
export interface RbacMatrix {
  version: 1;
  updatedAt: string;
  updatedBy: number;
  groups: Record<string, RbacPermissionSet>;
}

export interface PermissionsResponse {
  matrix: RbacMatrix | null;
  /** Env-derived super-admin group IDs (read-only, cannot be edited away). */
  superAdminGroups: number[];
}

// ---------------------------------------------------------------------------
// Versioning / changelog (v1.1)
// ---------------------------------------------------------------------------

export type VersionAction =
  | 'create'
  | 'update'
  | 'rotate'
  | 'delete'
  | 'restore'
  | 'revert'
  | 'purge';

export interface VersionFieldDiff {
  field: string;
  from: unknown;
  to: unknown;
}

/**
 * One entry in a client's version history, stored in KV under
 * `version:{clientId}:{seq (zero-padded)}`. NEVER contains a secret:
 * snapshot is the summary shape and rotate diffs are redacted.
 */
export interface VersionRecord {
  clientId: string;
  seq: number;
  at: string;
  actor: number;
  actorEmail?: string;
  actorName?: string;
  action: VersionAction;
  diff: VersionFieldDiff[];
  /** Post-action policy state (secret stripped); null after delete/purge. */
  snapshot: BrokerClientPolicySummary | null;
  note?: string;
}

export interface VersionListResponse {
  clientId: string;
  versions: VersionRecord[];
}

export interface ChangelogResponse {
  entries: VersionRecord[];
  /** True when the underlying KV list was truncated (more history exists). */
  truncated: boolean;
}

export interface ErrorResponse {
  error: {
    code: string;
    message: string;
    requestId: string;
    fieldErrors?: Record<string, string>;
  };
}
